import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import * as XLSX from 'xlsx'

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
const typeLabel = { mortgage: 'Home Loan', auto: 'Auto Loan', student: 'Education Loan', personal: 'Personal Loan', credit: 'Credit Card' }

function fmtNum(n) {
  n = Math.round(parseFloat(n) || 0)
  if (n >= 10000000) return '₹' + (n / 10000000).toFixed(1) + ' Cr'
  if (n >= 100000) return '₹' + (n / 100000).toFixed(1) + ' L'
  if (n >= 1000) return '₹' + n.toLocaleString('en-IN')
  return '₹' + n
}

function fmtDate(d) {
  if (!d) return 'N/A'
  const parts = d.split('-')
  return MONTHS[parseInt(parts[1]) - 1] + ' ' + parts[0]
}

function pct(loan) {
  const bal = parseFloat(loan.balance)
  const orig = parseFloat(loan.original_amount) || bal
  return orig > 0 ? Math.min(100, Math.max(0, Math.round((1 - bal / orig) * 100))) : 0
}

function monthsLeft(loan) {
  const emi = parseFloat(loan.emi_amount)
  return emi > 0 ? Math.ceil(parseFloat(loan.balance) / emi) : null
}

function summaryTotals(loans) {
  const totalBalance = loans.reduce((s, l) => s + parseFloat(l.balance || 0), 0)
  const totalOriginal = loans.reduce((s, l) => s + parseFloat(l.original_amount || l.balance || 0), 0)
  const totalEmi = loans.reduce((s, l) => s + parseFloat(l.emi_amount || 0), 0)
  const weightedRate = loans.reduce((s, l) => s + parseFloat(l.interest_rate || 0) * parseFloat(l.balance || 0), 0)
  const avgRate = totalBalance > 0 ? weightedRate / totalBalance : 0
  const overallPct = totalOriginal > 0 ? Math.min(100, Math.round(((totalOriginal - totalBalance) / totalOriginal) * 100)) : 0
  return { totalBalance, totalOriginal, totalEmi, avgRate, overallPct }
}

// ─── PDF ──────────────────────────────────────────────────────────────────────

export function exportPDF(loans, emiPayments, userName) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  const { totalBalance, totalOriginal, totalEmi, avgRate, overallPct } = summaryTotals(loans)
  const now = new Date()
  const dateStr = now.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })

  const W = doc.internal.pageSize.getWidth()
  let y = 15

  // Header band
  doc.setFillColor(13, 13, 13)
  doc.rect(0, 0, W, 22, 'F')
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(14)
  doc.setTextColor(245, 240, 232)
  doc.text('DebtCrush — Financial Health Report', 14, 13)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8)
  doc.setTextColor(180, 170, 160)
  doc.text(`Generated: ${dateStr}  |  ${userName}`, 14, 19)
  y = 30

  // Summary section
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(9)
  doc.setTextColor(13, 13, 13)
  doc.text('SUMMARY', 14, y)
  y += 4

  const summaryRows = [
    ['Total Loans', loans.length.toString()],
    ['Total Debt (Remaining)', fmtNum(totalBalance)],
    ['Original Total', fmtNum(totalOriginal)],
    ['Paid Off', fmtNum(totalOriginal - totalBalance) + ' (' + overallPct + '%)'],
    ['Monthly EMI', fmtNum(totalEmi)],
    ['Avg Interest Rate', avgRate.toFixed(2) + '% p.a.'],
  ]

  autoTable(doc, {
    startY: y,
    head: [],
    body: summaryRows,
    theme: 'plain',
    styles: { fontSize: 8, cellPadding: 2, textColor: [13, 13, 13] },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55, textColor: [100, 90, 85] }, 1: { cellWidth: 60 } },
    margin: { left: 14 },
  })

  y = doc.lastAutoTable.finalY + 8

  // Loans table
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(9)
  doc.setTextColor(13, 13, 13)
  doc.text('LOAN DETAILS', 14, y)
  y += 3

  const loanRows = loans.map(l => [
    l.name,
    typeLabel[l.type] || l.type,
    fmtNum(l.balance),
    fmtNum(l.original_amount),
    l.interest_rate + '%',
    fmtNum(l.emi_amount) + '/mo',
    l.due_day ? l.due_day + 'th' : 'N/A',
    fmtDate(l.start_date),
    fmtDate(l.end_date),
    pct(l) + '%',
    monthsLeft(l) != null ? monthsLeft(l) + ' mo' : 'N/A',
  ])

  autoTable(doc, {
    startY: y,
    head: [['Name', 'Type', 'Balance', 'Original', 'Rate', 'EMI', 'Due', 'Start', 'End', 'Paid%', 'Mo Left']],
    body: loanRows,
    theme: 'striped',
    headStyles: { fillColor: [13, 13, 13], textColor: [245, 240, 232], fontSize: 7, fontStyle: 'bold' },
    bodyStyles: { fontSize: 7, textColor: [13, 13, 13] },
    alternateRowStyles: { fillColor: [245, 240, 232] },
    margin: { left: 14, right: 14 },
    columnStyles: { 0: { cellWidth: 28 }, 1: { cellWidth: 20 } },
  })

  y = doc.lastAutoTable.finalY + 8

  // EMI history table
  if (emiPayments.length > 0) {
    if (y > 230) { doc.addPage(); y = 15 }

    doc.setFont('helvetica', 'bold')
    doc.setFontSize(9)
    doc.setTextColor(13, 13, 13)
    doc.text('EMI PAYMENT HISTORY', 14, y)
    y += 3

    const loanById = Object.fromEntries(loans.map(l => [l.id, l.name]))
    const emiRows = [...emiPayments]
      .sort((a, b) => b.payment_month.localeCompare(a.payment_month))
      .map(p => [
        loanById[p.loan_id] || p.loan_id,
        p.payment_month,
        p.status === 'paid' ? 'Paid' : p.status === 'unpaid' ? 'Unpaid' : 'N/A',
        p.amount_paid > 0 ? fmtNum(p.amount_paid) : '—',
        p.paid_at ? new Date(p.paid_at).toLocaleDateString('en-IN') : '—',
      ])

    autoTable(doc, {
      startY: y,
      head: [['Loan Name', 'Month', 'Status', 'Amount Paid', 'Paid On']],
      body: emiRows,
      theme: 'striped',
      headStyles: { fillColor: [13, 13, 13], textColor: [245, 240, 232], fontSize: 7, fontStyle: 'bold' },
      bodyStyles: { fontSize: 7, textColor: [13, 13, 13] },
      alternateRowStyles: { fillColor: [245, 240, 232] },
      margin: { left: 14, right: 14 },
    })
  }

  // Footer on each page
  const pageCount = doc.internal.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i)
    doc.setFontSize(7)
    doc.setTextColor(160, 150, 140)
    doc.text(`Page ${i} of ${pageCount}  |  DebtCrush`, W / 2, doc.internal.pageSize.getHeight() - 8, { align: 'center' })
  }

  doc.save(`debtcrush-financial-health-report-${now.toISOString().slice(0, 10)}.pdf`)
}

// ─── Per-Loan Interest Certificate ───────────────────────────────────────────

export function generateLoanCertificate(loan, loanEmiPayments, userName) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  const W = doc.internal.pageSize.getWidth()
  const now = new Date()
  const dateStr = now.toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })

  // Current financial year (India: April–March)
  const fyStartYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1
  const fy = `${fyStartYear}-${String(fyStartYear + 1).slice(2)}`
  const fyLabel = `April ${fyStartYear} – March ${fyStartYear + 1}`

  const principal = parseFloat(loan.original_amount) || parseFloat(loan.balance)
  const balance = parseFloat(loan.balance)
  const emi = parseFloat(loan.emi_amount) || 0
  const rate = parseFloat(loan.interest_rate) || 0

  // Payment totals
  const paid = loanEmiPayments.filter(p => p.status === 'paid')
  const totalPaidAmt = paid.reduce((s, p) => s + parseFloat(p.amount_paid || emi), 0)
  const principalRepaid = Math.max(0, principal - balance)
  const interestPaid = Math.max(0, totalPaidAmt - principalRepaid)

  // FY-specific payments
  const fyStart = new Date(fyStartYear, 3, 1)
  const fyEnd = new Date(fyStartYear + 1, 2, 31)
  const fyPaid = paid.filter(p => {
    const [yr, mo] = p.payment_month.split('-').map(Number)
    const d = new Date(yr, mo - 1, 1)
    return d >= fyStart && d <= fyEnd
  })
  const fyTotalPaid = fyPaid.reduce((s, p) => s + parseFloat(p.amount_paid || emi), 0)

  // Estimate FY interest: use running amortization to find balance at FY start
  let runningBalance = principal
  const r = rate / 12 / 100
  // Walk through all paid months chronologically to find balance at FY start
  const allPaidSorted = [...paid].sort((a, b) => a.payment_month.localeCompare(b.payment_month))
  for (const p of allPaidSorted) {
    const [yr, mo] = p.payment_month.split('-').map(Number)
    const d = new Date(yr, mo - 1, 1)
    if (d >= fyStart) break
    const interest = runningBalance * r
    const principalPortion = Math.max(0, Math.min(parseFloat(p.amount_paid || emi) - interest, runningBalance))
    runningBalance = Math.max(0, runningBalance - principalPortion)
  }
  // Now compute FY principal and interest from that balance
  let fyBalance = runningBalance
  let fyPrincipalPaid = 0
  let fyInterestPaid = 0
  for (const p of fyPaid.sort((a, b) => a.payment_month.localeCompare(b.payment_month))) {
    const amt = parseFloat(p.amount_paid || emi)
    const intPortion = fyBalance * r
    const prinPortion = Math.max(0, Math.min(amt - intPortion, fyBalance))
    fyInterestPaid += Math.max(0, intPortion)
    fyPrincipalPaid += prinPortion
    fyBalance = Math.max(0, fyBalance - prinPortion)
  }

  // ── Header band ──
  doc.setFillColor(13, 13, 13)
  doc.rect(0, 0, W, 24, 'F')
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(14)
  doc.setTextColor(245, 240, 232)
  doc.text('Interest Certificate', 14, 12)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8)
  doc.setTextColor(180, 170, 160)
  doc.text('DebtCrush  ·  For Income Tax / Loan Reference Purposes', 14, 19)

  let y = 32

  // ── Borrower & FY info ──
  doc.setFillColor(237, 232, 220)
  doc.rect(14, y, W - 28, 18, 'F')
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(8.5)
  doc.setTextColor(13, 13, 13)
  doc.text('CERTIFICATE ISSUED TO', 18, y + 5.5)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  doc.text(userName, 18, y + 12)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(8.5)
  doc.text('FINANCIAL YEAR', W / 2 + 4, y + 5.5)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  doc.text(`FY ${fy}  (${fyLabel})`, W / 2 + 4, y + 12)
  y += 24

  // ── Loan Details ──
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(8)
  doc.setTextColor(100, 90, 80)
  doc.text('LOAN DETAILS', 14, y)
  y += 3

  autoTable(doc, {
    startY: y,
    head: [],
    body: [
      ['Loan Name', loan.name],
      ['Loan Type', typeLabel[loan.type] || loan.type || 'N/A'],
      ['Principal (Original Amount)', fmtNum(principal)],
      ['Current Outstanding Balance', fmtNum(balance)],
      ['Interest Rate', rate + '% per annum'],
      ['Monthly EMI', emi > 0 ? fmtNum(emi) + ' per month' : 'N/A'],
      ['EMI Due Day', loan.due_day ? loan.due_day + 'th of every month' : 'N/A'],
      ['Loan Start Date', fmtDate(loan.start_date)],
      ['End Date / Payoff Date', fmtDate(loan.end_date)],
    ],
    theme: 'plain',
    styles: { fontSize: 9, cellPadding: [2, 3], textColor: [13, 13, 13] },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 72, textColor: [80, 70, 65] },
      1: { cellWidth: 100 },
    },
    margin: { left: 14 },
  })
  y = doc.lastAutoTable.finalY + 8

  // ── All-time payment summary ──
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(8)
  doc.setTextColor(100, 90, 80)
  doc.text('CUMULATIVE PAYMENT SUMMARY (ALL TIME)', 14, y)
  y += 3

  autoTable(doc, {
    startY: y,
    head: [],
    body: [
      ['Total EMIs Paid', paid.length + ' payment' + (paid.length !== 1 ? 's' : '')],
      ['Total Amount Paid', fmtNum(totalPaidAmt)],
      ['Principal Repaid', fmtNum(principalRepaid)],
      ['Interest Paid (estimated)', fmtNum(interestPaid)],
    ],
    theme: 'plain',
    styles: { fontSize: 9, cellPadding: [2, 3], textColor: [13, 13, 13] },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 72, textColor: [80, 70, 65] },
      1: { cellWidth: 100 },
    },
    margin: { left: 14 },
  })
  y = doc.lastAutoTable.finalY + 8

  // ── FY summary ──
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(8)
  doc.setTextColor(100, 90, 80)
  doc.text(`PAYMENT SUMMARY FOR FY ${fy}`, 14, y)
  y += 3

  autoTable(doc, {
    startY: y,
    head: [],
    body: [
      ['EMIs Paid in FY', fyPaid.length + ' payment' + (fyPaid.length !== 1 ? 's' : '')],
      ['Total Paid in FY', fmtNum(fyTotalPaid)],
      ['Principal Repaid in FY', fmtNum(fyPrincipalPaid)],
      ['Interest Paid in FY (estimated)', fmtNum(fyInterestPaid)],
    ],
    theme: 'plain',
    styles: { fontSize: 9, cellPadding: [2, 3], textColor: [13, 13, 13] },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 72, textColor: [80, 70, 65] },
      1: { cellWidth: 100 },
    },
    margin: { left: 14 },
  })
  y = doc.lastAutoTable.finalY + 8

  // ── Monthly payment table for FY ──
  if (fyPaid.length > 0) {
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(8)
    doc.setTextColor(100, 90, 80)
    doc.text(`MONTH-WISE PAYMENTS IN FY ${fy}`, 14, y)
    y += 3

    // Rebuild running balance for table
    let mb = runningBalance
    const tableRows = fyPaid
      .sort((a, b) => a.payment_month.localeCompare(b.payment_month))
      .map(p => {
        const [yr, mo] = p.payment_month.split('-').map(Number)
        const monthLabel = MONTHS[mo - 1] + ' ' + yr
        const amt = parseFloat(p.amount_paid || emi)
        const intPortion = mb * r
        const prinPortion = Math.max(0, Math.min(amt - intPortion, mb))
        mb = Math.max(0, mb - prinPortion)
        return [monthLabel, fmtNum(amt), fmtNum(prinPortion), fmtNum(Math.max(0, intPortion)), fmtNum(mb)]
      })

    autoTable(doc, {
      startY: y,
      head: [['Month', 'Amount Paid', 'Principal', 'Interest', 'Balance After']],
      body: tableRows,
      theme: 'striped',
      headStyles: { fillColor: [13, 13, 13], textColor: [245, 240, 232], fontSize: 7.5, fontStyle: 'bold' },
      bodyStyles: { fontSize: 7.5, textColor: [13, 13, 13] },
      alternateRowStyles: { fillColor: [245, 240, 232] },
      margin: { left: 14, right: 14 },
    })
    y = doc.lastAutoTable.finalY + 8
  }

  // ── Disclaimer ──
  if (y > 240) { doc.addPage(); y = 15 }
  doc.setFillColor(245, 240, 232)
  doc.rect(14, y, W - 28, 20, 'F')
  doc.setFont('helvetica', 'italic')
  doc.setFontSize(7)
  doc.setTextColor(100, 90, 80)
  const disc = 'Note: Interest figures are approximate estimates based on standard amortization calculation using the recorded EMI payments and current outstanding balance. For official tax purposes under Section 24(b) or 80C of the Income Tax Act, please obtain the certified interest certificate directly from your lender/bank.'
  const lines = doc.splitTextToSize(disc, W - 36)
  doc.text(lines, 18, y + 5)

  // ── Footer ──
  const pageCount = doc.internal.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(7)
    doc.setTextColor(160, 150, 140)
    doc.text(
      `Generated on ${dateStr}  ·  Page ${i} of ${pageCount}  ·  DebtCrush`,
      W / 2, doc.internal.pageSize.getHeight() - 8, { align: 'center' }
    )
  }

  const safeName = loan.name.replace(/[^a-z0-9]/gi, '-').toLowerCase()
  doc.save(`debtcrush-interest-cert-${safeName}-FY${fy}.pdf`)
}

// ─── All-Loans Interest Certificate ──────────────────────────────────────────

export function exportInterestCertificate(loans, emiPayments, userName, fy) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  const W = doc.internal.pageSize.getWidth()
  const now = new Date()
  const dateStr = now.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })

  // Parse FY e.g. "2024-25" → April 2024 to March 2025
  const fyParts = fy.split('-')
  const fyStart = new Date(parseInt(fyParts[0]), 3, 1)   // April 1
  const fyEnd = new Date(parseInt(fyParts[0]) + 1, 2, 31) // March 31

  // Header band
  doc.setFillColor(13, 13, 13)
  doc.rect(0, 0, W, 22, 'F')
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(13)
  doc.setTextColor(245, 240, 232)
  doc.text('Interest Certificate', 14, 12)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8)
  doc.setTextColor(180, 170, 160)
  doc.text('DebtCrush  |  For Income Tax / Home Loan Purposes', 14, 18)

  let y = 30

  // Borrower & FY info
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  doc.setTextColor(13, 13, 13)
  const infoRows = [
    ['Borrower Name', userName],
    ['Financial Year', `${fy} (${fyStart.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} to ${fyEnd.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })})`],
    ['Generated On', dateStr],
  ]
  autoTable(doc, {
    startY: y, head: [], body: infoRows, theme: 'plain',
    styles: { fontSize: 9, cellPadding: 2, textColor: [13, 13, 13] },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 50, textColor: [100, 90, 85] }, 1: { cellWidth: 100 } },
    margin: { left: 14 },
  })
  y = doc.lastAutoTable.finalY + 8

  // Compute interest per loan for the FY
  const loanRows = []
  let grandPrincipal = 0, grandInterest = 0, grandTotal = 0

  for (const loan of loans) {
    const emi = parseFloat(loan.emi_amount) || 0
    const rate = parseFloat(loan.interest_rate) || 0

    // Count paid EMIs in the FY
    const paidInFY = emiPayments.filter(p => {
      if (p.loan_id !== loan.id || p.status !== 'paid') return false
      const [yr, mo] = p.payment_month.split('-').map(Number)
      const d = new Date(yr, mo - 1, 1)
      return d >= fyStart && d <= fyEnd
    })

    const monthsPaid = paidInFY.length
    const totalPaid = paidInFY.reduce((s, p) => s + parseFloat(p.amount_paid || emi), 0)

    const r = rate / 12 / 100
    const principalPaid = totalPaid - Math.max(0, totalPaid * (r * monthsPaid / (1 + r * monthsPaid)))

    grandPrincipal += principalPaid
    grandInterest += totalPaid - principalPaid
    grandTotal += totalPaid

    loanRows.push([
      loan.name,
      typeLabel[loan.type] || loan.type,
      rate + '%',
      fmtNum(emi),
      monthsPaid,
      fmtNum(principalPaid),
      fmtNum(totalPaid - principalPaid),
      fmtNum(totalPaid),
    ])
  }

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(9)
  doc.setTextColor(13, 13, 13)
  doc.text('LOAN-WISE INTEREST & PRINCIPAL SUMMARY', 14, y)
  y += 3

  autoTable(doc, {
    startY: y,
    head: [['Loan Name', 'Type', 'Rate', 'EMI', 'Months Paid', 'Principal', 'Interest', 'Total Paid']],
    body: [
      ...loanRows,
      [{ content: 'TOTAL', colSpan: 5, styles: { fontStyle: 'bold', fillColor: [13, 13, 13], textColor: [245, 240, 232] } },
       { content: fmtNum(grandPrincipal), styles: { fontStyle: 'bold', fillColor: [13, 13, 13], textColor: [245, 240, 232] } },
       { content: fmtNum(grandInterest), styles: { fontStyle: 'bold', fillColor: [13, 13, 13], textColor: [245, 240, 232] } },
       { content: fmtNum(grandTotal), styles: { fontStyle: 'bold', fillColor: [13, 13, 13], textColor: [245, 240, 232] } }],
    ],
    theme: 'striped',
    headStyles: { fillColor: [61, 74, 92], textColor: [245, 240, 232], fontSize: 7, fontStyle: 'bold' },
    bodyStyles: { fontSize: 7.5, textColor: [13, 13, 13] },
    alternateRowStyles: { fillColor: [245, 240, 232] },
    margin: { left: 14, right: 14 },
  })

  y = doc.lastAutoTable.finalY + 10

  // Disclaimer
  doc.setFont('helvetica', 'italic')
  doc.setFontSize(7)
  doc.setTextColor(120, 110, 100)
  const disclaimer = 'Note: This certificate is generated based on EMI payment records in DebtCrush. Interest figures are approximate. Please consult your bank\'s official statement for precise tax deduction purposes under Section 24(b) or 80C of the Income Tax Act.'
  const lines = doc.splitTextToSize(disclaimer, W - 28)
  doc.text(lines, 14, y)

  // Footer
  const pageCount = doc.internal.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(7)
    doc.setTextColor(160, 150, 140)
    doc.text(`Page ${i} of ${pageCount}  |  DebtCrush Interest Certificate`, W / 2, doc.internal.pageSize.getHeight() - 8, { align: 'center' })
  }

  doc.save(`debtcrush-interest-certificate-FY${fy}-${now.toISOString().slice(0, 10)}.pdf`)
}

// ─── Excel ────────────────────────────────────────────────────────────────────

export function exportExcel(loans, emiPayments, userName) {
  const wb = XLSX.utils.book_new()
  const now = new Date()
  const { totalBalance, totalOriginal, totalEmi, avgRate, overallPct } = summaryTotals(loans)

  // Summary sheet
  const summaryData = [
    ['DebtCrush — Financial Health Report'],
    [`Generated: ${now.toLocaleDateString('en-IN')}  |  ${userName}`],
    [],
    ['Metric', 'Value'],
    ['Total Loans', loans.length],
    ['Total Debt (Remaining)', totalBalance],
    ['Original Total', totalOriginal],
    ['Amount Paid Off', totalOriginal - totalBalance],
    ['% Paid Off', overallPct + '%'],
    ['Monthly EMI Total', totalEmi],
    ['Avg Interest Rate (weighted)', avgRate.toFixed(2) + '%'],
  ]
  const wsSummary = XLSX.utils.aoa_to_sheet(summaryData)
  wsSummary['!cols'] = [{ wch: 35 }, { wch: 20 }]
  XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary')

  // Loans sheet
  const loanHeaders = ['Name', 'Type', 'Current Balance (₹)', 'Original Amount (₹)', 'Interest Rate (%)', 'Monthly EMI (₹)', 'Due Day', 'Start Date', 'End Date', '% Paid Off', 'Months Remaining']
  const loanRows = loans.map(l => [
    l.name,
    typeLabel[l.type] || l.type,
    parseFloat(l.balance),
    parseFloat(l.original_amount),
    parseFloat(l.interest_rate),
    parseFloat(l.emi_amount),
    l.due_day || '',
    fmtDate(l.start_date),
    fmtDate(l.end_date),
    pct(l),
    monthsLeft(l) ?? '',
  ])
  const wsLoans = XLSX.utils.aoa_to_sheet([loanHeaders, ...loanRows])
  wsLoans['!cols'] = [{ wch: 25 }, { wch: 16 }, { wch: 20 }, { wch: 20 }, { wch: 18 }, { wch: 16 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 16 }]
  XLSX.utils.book_append_sheet(wb, wsLoans, 'Loans')

  // EMI history sheet
  if (emiPayments.length > 0) {
    const loanById = Object.fromEntries(loans.map(l => [l.id, l.name]))
    const emiHeaders = ['Loan Name', 'Month', 'Status', 'Amount Paid (₹)', 'Paid On']
    const emiRows = [...emiPayments]
      .sort((a, b) => b.payment_month.localeCompare(a.payment_month))
      .map(p => [
        loanById[p.loan_id] || p.loan_id,
        p.payment_month,
        p.status === 'paid' ? 'Paid' : p.status === 'unpaid' ? 'Unpaid' : 'N/A',
        parseFloat(p.amount_paid || 0),
        p.paid_at ? new Date(p.paid_at).toLocaleDateString('en-IN') : '',
      ])
    const wsEmi = XLSX.utils.aoa_to_sheet([emiHeaders, ...emiRows])
    wsEmi['!cols'] = [{ wch: 25 }, { wch: 14 }, { wch: 10 }, { wch: 18 }, { wch: 14 }]
    XLSX.utils.book_append_sheet(wb, wsEmi, 'EMI History')
  }

  XLSX.writeFile(wb, `debtcrush-financial-health-report-${now.toISOString().slice(0, 10)}.xlsx`)
}
